<script setup lang="ts">
import merge from 'lodash/merge';

interface FormSingleSelect
{
    content: {
        value: {
            question: string;
            answers: Answer[];
        };
    };
}

interface Answer
{
    id: number;
    text: string;
    isCorrect: boolean;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const defaults: any = {
    question: '',
    answers: []
};

const form = computed((): FormType<FormSingleSelect> =>
{
    return props.modelValue;
});

onBeforeMount((): void =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.answers.length)
    {
        form.value.content.value.answers = [{
            id: 1,
            text: '',
            isCorrect: true
        }];
    }
});

function addAnswer(): void
{
    const ids = form.value.content.value.answers.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.answers.length > 0 ? Math.max(...ids) : 1;

    form.value.content.value.answers.push({
        id: maxId + 1,
        text: '',
        isCorrect: false
    });
}

function removeAnswer(index: number): void
{
    const items = form.value.content.value.answers.slice();

    items.splice(index, 1);
    form.value.content.value.answers = items;

    form.value.$errors.clear(`content.value.answers[${index}]`);
}

function answerUp(index: number): void
{
    if (index > 0)
    {
        const answers = form.value.content.value.answers.splice(index, 1);

        form.value.content.value.answers.splice(index - 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}

function answerDown(index: number): void
{
    if (index < form.value.content.value.answers.length - 1)
    {
        const answers = form.value.content.value.answers.splice(index, 1);

        form.value.content.value.answers.splice(index + 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}

function isCorrectChanged(id: number): void
{
    const clickedAnswer = form.value.content.value.answers.find((x: { id: number }) => x.id == id);
    const clickedAnswerValue = clickedAnswer.isCorrect;

    const count = form.value.content.value.answers.filter((item) =>
    {
        return item.isCorrect;
    }).length;

    if (count == 1)
    {
        const correctAnswer = form.value.content.value.answers.find(x => x.isCorrect);

        if (correctAnswer.id == id)
            return;
    }

    form.value.content.value.answers.find((x: { id: number }) => x.id == id).isCorrect = !clickedAnswerValue;

    for (const answer of form.value.content.value.answers)
    {
        if (answer.id != id)
        {
            answer.isCorrect = false;
        }
    }

    form.value.$errors.clear('content.value.answers');
}

watch(() => form.value.content.value, (value: any): void =>
{
    triggetModel();
}, { deep: true });

function triggetModel(): void
{
    form.value.$errors.clear('content.value.question');
    emit('update:modelValue', form.value);
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Pytanie]]]')" :invalid-feedback="form.$errors.first('content.value.question')" :state="form.$errors.state('content.value.question')" required>
        <RichTextEditor
            :id="seconds('question-' + form.id)"
            v-model="form.content.value.question"
            name="question"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Odpowiedzi]]]')" :invalid-feedback="form.$errors.first('content.value.answers')" :state="form.$errors.state('content.value.answers')">
        <div v-for="(answer, i) in form.content.value.answers" :key="answer.id" class="mb-3 g-0 row">
            <div class="col-md-11 col-sm-10">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                    </div>
                    <IdeoFormTextarea v-model="answer.text" :name="`content.value.answers[${i}]`" :rows="2" :placeholder="$t('[[[Odpowiedź]]]')" :data-cy="`answer-${i}`" />
                    <div class="input-group-append">
                        <ActionButton :variant="answer.isCorrect ? 'success' : 'secondary'" icon="far fa-check" :title="$t('[[[Poprawna odpowiedź]]]')" data-cy="isCorrect" @click="isCorrectChanged(answer.id)" />
                        <ActionButton
                            variant="danger"
                            icon="far fa-trash"
                            :title="$t('[[[Usuń odpowiedź]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            :disabled="form.content.value.answers.length < 2"
                            @click="removeAnswer(i)"
                        />
                    </div>
                </div>
            </div>
            <div class="col-md-1 col-sm-2 d-flex align-items-start">
                <ActionButton
                    variant="secondary"
                    icon="far fa-arrow-up"
                    class="ms-auto"
                    :title="$t('[[[Wyżej]]]') "
                    :disabled="i == 0"
                    @click="answerUp(i)"
                />
                <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="i == form.content.value.answers.length - 1" @click="answerDown(i)" />
            </div>
            <small v-if="!form.$errors.state(`content.value.answers[${i}]`)" class="form-text text-danger">{{ form.$errors.first(`content.value.answers[${i}]`) }}</small>
        </div>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Dodaj odpowiedź]]]')"
                data-cy="addAnswer"
                @click="addAnswer()"
            />
        </div>
    </IdeoFormGroup>
</template>
